import { type FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text
} from '@cencosud-cencommerce/paris-uikit';

import { useCart } from '@/Application/Selectors/cart/useCart';
import type { Address } from '@/modules/address/domain/Address';
import type { Region } from '@/modules/region/domain/Region';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import { useShippingPageContext } from '@/Pages/ShippingPage/ShippingPageContext';
import { useSetDefaultAddress } from '@/sections/address/hooks/mutators/useSetDefaultAddress';
import {
  LABEL_CHANGE_ADDRESS_ERROR,
  LABEL_CHANGE_ADDRESS_SUCCESS
} from '@/sections/address/hooks/mutators/useSetDefaultAddress/useSetDefaultAddress';
import { useDefaultAddress } from '@/sections/address/hooks/queries/useDefaultAddress';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import useCommunesOptions from '@/sections/region/hooks/useCommunesOption';
import useRegions from '@/sections/region/hooks/useRegions';
import useRegionsOptions from '@/sections/region/hooks/useRegionsOptions';
import useGetGeneralShippingMethods from '@/sections/shippingMethods/hooks/useGetGeneralShippingMethods';

import { useCreateShippingAddress } from '../../hooks/mutators/useCreateShippingAddress';

import { AddressItem } from './partial/AddressItem';

interface Props {
  isOpen: boolean;
  onClose: VoidFunction;
  onCreateNewAddress: VoidFunction;
  onEditAddress: (address: Address) => void;
  handleSetOptions: () => void;
  onShippingOptionsModalOpen: () => void;
}

const SelectionAddressModal: FC<Props> = ({
  isOpen,
  onClose,
  onCreateNewAddress,
  onEditAddress,
  handleSetOptions,
  onShippingOptionsModalOpen
}) => {
  const { completeSkuHistory } = useShippingPageContext();
  const { data: cart } = useCart();
  const regionOptions = useRegionsOptions();
  const communeOptions = useCommunesOptions(
    cart?.shippingAddress?.region as number
  );
  const region = regionOptions.find(
    (regionOption) => regionOption.value === cart?.shippingAddress?.region
  );
  const commune = communeOptions.find(
    (communeOption) => communeOption.value === cart?.shippingAddress?.commune
  );

  const { refetchShippingMethods } = useGetGeneralShippingMethods();
  const { trigger: setDefaultAddress } = useSetDefaultAddress();
  const { data: defaultAddress } = useDefaultAddress();
  const { trigger: setShippingAddress, isLoading } =
    useCreateShippingAddress(false);

  const {
    customer: { data, refetch: refetchCustomerData }
  } = useCustomerContext();
  const [selectedAddressId, setSelectedAddresId] = useState<
    Address['id'] | undefined
  >();

  useEffect(() => {
    if (defaultAddress && data?.addresses?.length) {
      const customerAddress = data.addresses.find(
        (address) => defaultAddress.alias === address.alias
      );
      setSelectedAddresId(customerAddress?.id);
    }
  }, [defaultAddress, data?.addresses]);

  const { getRegionById, getCommuneById } = useRegions();

  const handleOnSubmit = async () => {
    const customerAdresses = data?.addresses ?? [];
    const selectedAddress = customerAdresses?.find(
      (address) => address.id === selectedAddressId
    );
    if (!selectedAddress) return;

    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.ModalMyAddress,
        'event-interactive-tag': ShippingEventInteractiveTags.ClickConfirm
      }
    });
    await setShippingAddress(selectedAddress as Address);

    const region = getRegionById(selectedAddress?.region?.toString() ?? '');
    const regionName = region?.name;
    const communeName = getCommuneById(
      region as Region,
      selectedAddress?.commune ?? ''
    )?.name;

    await setDefaultAddress(selectedAddressId as Address['id'])
      .then(() => {
        sendChangeAddressEvent(
          LABEL_CHANGE_ADDRESS_SUCCESS,
          regionName,
          communeName
        );
        onShippingOptionsModalOpen();
      })
      .catch((e) => {
        console.error(e);
        sendChangeAddressEvent(
          LABEL_CHANGE_ADDRESS_ERROR,
          regionName,
          communeName
        );
      });
    onClose();
    await refetchShippingMethods(completeSkuHistory);
    await refetchCustomerData();
    handleSetOptions();
  };

  const handleNewAddress = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.ModalMyAddress,
        'event-interactive-tag': ShippingEventInteractiveTags.AddAddress
      }
    });
    onCreateNewAddress();
  };

  const sendChangeAddressEvent = (
    feedback: string,
    region?: string,
    commune?: string
  ) => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag': feedback,
        'event-interactive-accion':
          ShippingEventInteractiveActions.ChangeAddress,
        'event-interactive-comuna': commune,
        'event-interactive-region': region
      }
    });
  };
  const sendMyAddressesCloseEvent = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.ModalMyAddress,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag':
          ShippingEventInteractiveTags.ClickCloseMyAddressesModal
      }
    });
  };

  return (
    <div className="co-z-40">
      <Modal isOpen={isOpen} className="co-z-40">
        <ModalHeader
          onClose={() => {
            sendMyAddressesCloseEvent();
            onClose();
          }}
          title="Mis direcciones"
          icon={{ name: 'place', fill: 'primary-500' }}
        />
        <ModalBody className="co-flex co-flex-col co-gap-4">
          <div className="co-flex co-items-center co-justify-between">
            <Text variant="body1" weight={600}>
              {commune?.label}, {region?.label}
            </Text>
            <Button
              variant="tertiary"
              iconLeft="add"
              onClick={handleNewAddress}
            >
              Agregar dirección
            </Button>
          </div>
          <Alert
            status="info"
            body="Para eliminar una dirección ingresa a mi cuenta"
          />
          <div className="co-flex co-flex-col co-gap-2">
            {data?.addresses.map((address) => (
              <AddressItem
                key={address.id}
                address={address}
                checked={selectedAddressId === address.id}
                onClick={setSelectedAddresId}
                onEdit={onEditAddress}
              />
            ))}
          </div>
        </ModalBody>
        <ModalFooter
          primaryButton={{
            label: 'Continuar',
            onClick: handleOnSubmit,
            loading: isLoading,
            disabled: defaultAddress?.id === selectedAddressId
          }}
        />
      </Modal>
    </div>
  );
};

export default SelectionAddressModal;
